import React, { useState, useEffect } from "react";
import { FileText, Plus, Pencil, Trash2, Loader2, Copy, X } from "lucide-react";
import { getDocTemplates, createDocTemplate, updateDocTemplate, deleteDocTemplate } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const CATS = ["Tenancy", "Sales", "Management", "Letters", "Other"];
const empty = { name: "", category: "Letters", type: "letter", body: "" };
const input = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

const AdminTemplates = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => { setLoading(true); try { setItems(await getDocTemplates()); } catch { toast({ title: "Couldn't load templates" }); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setF({ name: t.name, category: t.category || "Letters", type: t.type || "letter", body: t.body || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.body.trim()) { toast({ title: "Add a name and body" }); return; }
    setSaving(true);
    try {
      if (editing) { const u = await updateDocTemplate(editing.id, f); setItems((xs) => xs.map((x) => x.id === editing.id ? u : x)); toast({ title: "Template updated" }); }
      else { const c = await createDocTemplate(f); setItems((xs) => [c, ...xs]); toast({ title: "Template created" }); }
      setOpen(false);
    } catch { toast({ title: "Couldn't save template" }); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await deleteDocTemplate(t.id); setItems((xs) => xs.filter((x) => x.id !== t.id)); toast({ title: "Template deleted" }); }
    catch { toast({ title: "Couldn't delete" }); }
  };

  const copyBody = (t) => { navigator.clipboard?.writeText(t.body || ""); toast({ title: "Copied to clipboard", description: "Placeholders like {{partyB}} will need filling in." }); };

  const grouped = CATS.map((c) => ({ cat: c, list: items.filter((t) => (t.category || "Other") === c) })).filter((g) => g.list.length);

  if (loading) return <div className="flex items-center justify-center py-24 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading templates…</div>;

  return (
    <div data-testid="admin-templates">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><FileText size={19} className="text-[#b8935f]" /> Templates ({items.length})</h3>
          <p className="text-[13px] text-[#8a8a8a] mt-1">Reusable UK agency letters &amp; agreements. Use placeholders like <code className="text-[#b8935f]">{"{{partyA}}"}</code>, <code className="text-[#b8935f]">{"{{partyB}}"}</code>, <code className="text-[#b8935f]">{"{{propertyTitle}}"}</code>, <code className="text-[#b8935f]">{"{{amount}}"}</code>.</p>
        </div>
        <button onClick={openNew} data-testid="template-new-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1c1c1e] text-white text-[13px] font-500 hover:bg-black transition-colors"><Plus size={15} /> New template</button>
      </div>

      {grouped.map(({ cat, list }) => (
        <div key={cat} className="mb-6">
          <p className="text-[12px] uppercase tracking-wide text-[#a0a0a0] mb-2">{cat}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {list.map((t) => (
              <div key={t.id} className="bg-white rounded-xl border border-[#f0ece5] p-4" data-testid="template-card">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[14px] font-500 text-[#2b2b2b]">{t.name}{t.builtIn && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[#faf7f2] border border-[#f0ece5] text-[#a0a0a0]">built-in</span>}</p>
                </div>
                <p className="text-[12px] text-[#8a8a8a] mt-1.5 line-clamp-3 whitespace-pre-line">{t.body}</p>
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => setPreview(t)} data-testid="template-preview-btn" className="text-[12px] px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f]">Preview</button>
                  <button onClick={() => copyBody(t)} className="text-[12px] px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] inline-flex items-center gap-1"><Copy size={13} /> Copy</button>
                  <button onClick={() => openEdit(t)} data-testid="template-edit-btn" className="text-[12px] px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] inline-flex items-center gap-1"><Pencil size={13} /> Edit</button>
                  {!t.builtIn && <button onClick={() => remove(t)} data-testid="template-delete-btn" className="ml-auto text-[#c9c2b6] hover:text-red-500"><Trash2 size={14} /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[88vh] overflow-y-auto">
          <DialogTitle className="font-display text-[22px] font-300 text-[#2b2b2b] mb-3">{editing ? "Edit template" : "New template"}</DialogTitle>
          <form onSubmit={save} className="space-y-3" data-testid="template-form">
            <input placeholder="Template name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={input} data-testid="template-name" />
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="template-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <textarea placeholder="Template body… use {{partyA}}, {{partyB}}, {{propertyTitle}}, {{amount}}" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={10} className="w-full px-3 py-2 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none font-mono" data-testid="template-body" />
            <button type="submit" disabled={saving} className="w-full h-[48px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="template-save">{saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} {editing ? "Save changes" : "Create template"}</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-[560px] max-h-[88vh] overflow-y-auto">
          <DialogTitle className="font-display text-[22px] font-300 text-[#2b2b2b] mb-1">{preview?.name}</DialogTitle>
          <p className="text-[12px] text-[#a0a0a0] mb-3">{preview?.category}</p>
          <pre className="text-[13px] text-[#4a4a4a] whitespace-pre-wrap font-sans bg-[#faf7f2] border border-[#f0ece5] rounded-xl p-4">{preview?.body}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTemplates;
